import { useAuth } from '../auth/AuthContext';
import type { AnalysisCategory, AnalysisResult } from '../types';

const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8000';

interface RequestOptions extends RequestInit {
  headers?: Record<string, string>;
}

interface AuthContext {
  isAuthenticated: boolean;
  getAccessToken: () => Promise<string>;
}

class ApiClient {
  private baseURL: string;
  private _authContext?: AuthContext;

  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Make authenticated API request with improved token handling
  async request(endpoint: string, options: RequestOptions = {}): Promise<Response> {
    const url = `${this.baseURL}${endpoint}`;
    
    const makeRequest = async (token?: string): Promise<Response> => {
      const headers: Record<string, string> = { ...options.headers };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 1800000);

      try {
        const response = await fetch(url, { 
          ...options, 
          headers,
          signal: controller.signal 
        });
        clearTimeout(timeoutId);
        return response;
      } catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === 'AbortError') {
          throw new Error('Request timed out - video processing is taking longer than expected. Please try a shorter video or contact support.');
        }
        throw error;
      }
    };

    try {
      const authContext = this.getAuthContext();
      
      if (authContext?.isAuthenticated) {
        let token = await authContext.getAccessToken();
        let response = await makeRequest(token);
        
        if (response.status === 401) {
          console.warn('401 received, attempting token refresh and retry');
          
          try {
            // Force fresh token acquisition
            token = await authContext.getAccessToken();
            response = await makeRequest(token);
            
            if (response.status === 401) {
              console.error('Authentication failed even after token refresh');
              throw new Error('Authentication expired - please sign in again');
            }            
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            throw new Error('Authentication required - please sign in again');
          }
        }
        return response;
      } else {
        // Not authenticated
        return makeRequest();
      }
      
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  getAuthContext(): AuthContext | undefined {
    return this._authContext;
  }

  setAuthContext(context: AuthContext): void {
    this._authContext = context;
  }

  // Convenience methods for common HTTP verbs
  async get(endpoint: string, options: RequestOptions = {}): Promise<Response> {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  async post(endpoint: string, data: object | FormData, options: RequestOptions = {}): Promise<Response> {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: data instanceof FormData ? data : JSON.stringify(data),
      headers: data instanceof FormData ? options.headers : { 'Content-Type': 'application/json', ...options.headers },
    });
  }

  async put(endpoint: string, data: object, options: RequestOptions = {}): Promise<Response> {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint: string, options: RequestOptions = {}): Promise<Response> {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  async getHealth(): Promise<{ status: string }> {
    const response = await this.get('/api/health');
    return response.json();
  }

  async getCategories(): Promise<{ categories: { [key: string]: AnalysisCategory } }> {
    const response = await this.get('/api/categories');
    return response.json();
  }

  async analyzeFiles(formData: FormData): Promise<AnalysisResult> {
    const response = await this.post('/api/analyze', formData);
    return response.json();
  }

  async queryTranscript(data: { messages: { role: string; content: string }[]; transcript: string; category: string }): Promise<{ response: string }> {
    const response = await this.post('/api/query-transcript', data);
    return response.json();
  }
}

// Create singleton instance
const apiClient = new ApiClient();

// Custom hook that provides authenticated API client
export const useApiClient = () => {
  const auth = useAuth();
  
  // Update the client with current auth context
  apiClient.setAuthContext(auth);
  
  return apiClient;
};
